import React, {FC, memo, useState} from "react";
import {ApolloError} from "@apollo/client";
import {Card, Modal, Space} from "antd";
import {FormikProps} from "formik";
import {grey} from "@ant-design/colors";
import {
    Booking,
    EditTemplateInput,
    GetTemplateByIdQuery,
    GetTemplatesQuery,
    MailDataType,
    ScheduledPayment,
    SendMailToSelectionOfCourseInput,
    useGetTemplateByIdQuery,
    useGetBookingsByCourseQuery,
    GetTemplatesWithPaginationQuery,
    TemplateType,
} from "../../../generated/graphql";
import EmailPreview from "./EmailPreview";
import EmailEditor from "./EmailEditor";
import {marginS, marginXs} from "../../../styles/layout";
import EmailFooter from "./EmailFooter";
import ReceiversBar from "./ReceiversBar";
import {SelectionType} from "./EmailTemplatePicker";
import UploadComponent from "./UploadComponent";
import CourseBookingTypeSelector, {NotifySelection} from "./SendMailHandlers/CourseBookingTypeSelector";

type EmailProps = {
    templateId: string;
    mailDataType: MailDataType;
    selectedTemplate?: GetTemplateByIdQuery["template"] | GetTemplatesWithPaginationQuery["templatesWithPagination"]["items"];
    setSelectedTemplate: (template: GetTemplatesQuery["templates"][0] | GetTemplatesWithPaginationQuery["templatesWithPagination"]["items"][0] | undefined) => void;
    selection: Array<SelectionType>;
    closeAll?: () => void;
    loading?: boolean;
};

export type DbTemplate = {
    data: GetTemplateByIdQuery | undefined;
    loading: boolean;
    error?: ApolloError;
    refetch: () => void;
};

const Email: FC<EmailProps> = ({templateId, mailDataType, selectedTemplate, setSelectedTemplate, selection, closeAll, loading}) => {
    const [editMode, setEditMode] = React.useState(false);
    const [formikProps, setFormikProps] = useState<FormikProps<EditTemplateInput>>();
    // Kanonischer, ungespeicherter Entwurf (#820). Er liegt hier und nicht in der Formik-Instanz,
    // weil der Wechsel Editor <-> Vorschau den Editor samt Formik unmountet: Editor-Initialwerte,
    // Vorschau und Send-Payload speisen sich alle aus diesem einen State.
    const [draft, setDraft] = useState<EditTemplateInput>();
    const [attachments, setAttachments] = useState<SendMailToSelectionOfCourseInput["attachments"]>();
    const [notifySelection, setNotifySelection] = useState<Array<NotifySelection>>(["notifyBookings"]); // Default: Buchungen sind ausgewählt
    const [disabledSendButton, setDisabledSendButton] = useState(false);
    const [receiverCount, setReceiverCount] = useState(0);
    const [attachInvoices, setAttachInvoices] = useState(false);
    const [attachReversalInvoices, setAttachReversalInvoices] = useState(false);

    // Für Kurse: Buchungen laden um Invoice-Status zu berechnen
    const courseId = mailDataType === MailDataType.Course ? selection[0]?.id : undefined;
    const {data: courseBookingsData} = useGetBookingsByCourseQuery({
        skip: !courseId,
        variables: {id: courseId ?? "", options: {}},
        fetchPolicy: "cache-and-network",
    });

    // Funktion um Buchungen basierend auf notifySelection zu filtern
    const getFilteredCourseBookings = () => {
        if (!courseBookingsData?.getBookingsByCourse?.items) {
            return [];
        }

        const allBookings = courseBookingsData.getBookingsByCourse.items;
        const filtered: typeof allBookings = [];

        if (notifySelection.includes("notifyBookings")) {
            // Normale Buchungen: nicht auf Warteliste und nicht storniert
            filtered.push(...allBookings.filter((b) => (b.waitListCount ?? 0) === 0 && !b.canceled));
        }
        if (notifySelection.includes("notifyWaitlist")) {
            // Warteliste: waitListCount > 0
            filtered.push(...allBookings.filter((b) => (b.waitListCount ?? 0) > 0));
        }
        if (notifySelection.includes("notifyCancelations")) {
            // Stornierungen: canceled !== null
            filtered.push(...allBookings.filter((b) => b.canceled));
        }

        return filtered;
    };

    // Check if feature is available for this mailDataType
    const showInvoiceAttachments = mailDataType === MailDataType.Booking || mailDataType === MailDataType.ScheduledPayment || mailDataType === MailDataType.Course;

    // Check if invoices can be attached and get reason if not
    const invoiceAttachmentStatus = (() => {
        if (!showInvoiceAttachments) {
            return {enabled: false, reason: ""};
        }

        if (mailDataType === MailDataType.Course) {
            const filteredBookings = getFilteredCourseBookings();

            if (filteredBookings.length === 0) {
                return {enabled: false, reason: "Keine Buchungen ausgewählt"};
            }

            const allHaveInvoices = filteredBookings.every((booking) => booking.invoice?.id);
            const noneAreCanceled = filteredBookings.every((booking) => !booking.canceled);

            if (!allHaveInvoices) {
                return {enabled: false, reason: "Nicht alle zu benachrichtigenden Buchungen sind abgerechnet"};
            }

            if (!noneAreCanceled) {
                return {enabled: false, reason: "Es sind stornierte Buchungen dabei"};
            }

            return {enabled: true, reason: ""};
        }

        if (mailDataType === MailDataType.Booking) {
            const bookings = selection as Array<Booking>;
            const allHaveInvoices = bookings.every((booking) => booking.invoice?.id);
            const noneAreCanceled = bookings.every((booking) => !booking.canceled);

            if (!allHaveInvoices) {
                return {enabled: false, reason: "Nicht alle Buchungen sind abgerechnet"};
            }

            if (!noneAreCanceled) {
                return {enabled: false, reason: "Es sind stornierte Buchungen dabei"};
            }

            return {enabled: true, reason: ""};
        }

        // MailDataType.ScheduledPayment
        // Bei ScheduledPayments liegt die invoice direkt auf dem payment (nicht auf booking)
        // ScheduledPayments existieren nur für abgerechnete Buchungen
        const payments = selection as Array<ScheduledPayment>;
        const allHaveInvoices = payments.every((payment) => payment.invoice?.id);
        const noneAreCanceled = payments.every((payment) => !payment.booking?.canceled);

        if (!allHaveInvoices) {
            return {enabled: false, reason: "Nicht alle zugehörigen Buchungen sind abgerechnet"};
        }

        if (!noneAreCanceled) {
            return {enabled: false, reason: "Es sind stornierte Buchungen dabei"};
        }

        return {enabled: true, reason: ""};
    })();

    const reversalInvoiceAttachmentStatus = (() => {
        if (!showInvoiceAttachments) {
            return {enabled: false, reason: ""};
        }

        if (mailDataType === MailDataType.Course) {
            const filteredBookings = getFilteredCourseBookings();

            if (filteredBookings.length === 0) {
                return {enabled: false, reason: "Keine Buchungen ausgewählt"};
            }

            const allAreCanceled = filteredBookings.every((booking) => booking.canceled);
            const allHaveReversalInvoices = filteredBookings.every((booking) => booking.reversalInvoice?.id);

            if (!allAreCanceled) {
                return {enabled: false, reason: "Nicht alle zu benachrichtigenden Buchungen sind storniert"};
            }

            if (!allHaveReversalInvoices) {
                return {enabled: false, reason: "Nicht alle Buchungen haben eine Storno-Rechnung"};
            }

            return {enabled: true, reason: ""};
        }

        if (mailDataType === MailDataType.Booking) {
            const bookings = selection as Array<Booking>;
            const allAreCanceled = bookings.every((booking) => booking.canceled);
            const allHaveReversalInvoices = bookings.every((booking) => booking.reversalInvoice?.id);

            if (!allAreCanceled) {
                return {enabled: false, reason: "Nicht alle Buchungen sind storniert"};
            }

            if (!allHaveReversalInvoices) {
                return {enabled: false, reason: "Nicht alle Buchungen haben eine Storno-Rechnung"};
            }

            return {enabled: true, reason: ""};
        }

        // MailDataType.ScheduledPayment
        const payments = selection as Array<ScheduledPayment>;
        const allAreCanceled = payments.every((payment) => payment.booking?.canceled);
        const allHaveReversalInvoices = payments.every((payment) => payment.booking?.reversalInvoice?.id);

        if (!allAreCanceled) {
            return {enabled: false, reason: "Nicht alle zugehörigen Buchungen sind storniert"};
        }

        if (!allHaveReversalInvoices) {
            return {enabled: false, reason: "Nicht alle Buchungen haben eine Storno-Rechnung"};
        }

        return {enabled: true, reason: ""};
    })();

    // Berechne die Anzahl der Rechnungen für die Größenberechnung
    const invoiceCount = (() => {
        if (!showInvoiceAttachments) return 0;

        if (mailDataType === MailDataType.Course) {
            return getFilteredCourseBookings().length;
        }

        // Booking oder ScheduledPayment
        return selection.length;
    })();

    // Reset checkbox states when they become unavailable
    React.useEffect(() => {
        if (!invoiceAttachmentStatus.enabled && attachInvoices) {
            setAttachInvoices(false);
        }
    }, [invoiceAttachmentStatus.enabled, attachInvoices]);

    React.useEffect(() => {
        if (!reversalInvoiceAttachmentStatus.enabled && attachReversalInvoices) {
            setAttachReversalInvoices(false);
        }
    }, [reversalInvoiceAttachmentStatus.enabled, attachReversalInvoices]);

    // Der Entwurf gehoert zu genau einer Vorlage in genau einem geoeffneten Dialog:
    // Vorlagenwechsel, Empfaengertyp-Wechsel und Schliessen/Verwerfen setzen ihn zurueck (#820).
    const dialogOpen = Boolean(selectedTemplate);
    React.useEffect(() => {
        setDraft(undefined);
        setFormikProps(undefined);
    }, [templateId, mailDataType, dialogOpen]);

    const {
        data: dbTemplateByIdData,
        loading: dbLoadingTemplateById,
        error: dbErrorTemplateById,
        refetch: refetchDbTemplateById,
    } = useGetTemplateByIdQuery({
        skip: Boolean(!templateId),
        variables: {id: templateId},
        fetchPolicy: "network-only",
    });

    const dbTemplate: DbTemplate = {
        data: dbTemplateByIdData ?? undefined,
        loading: dbLoadingTemplateById,
        error: dbErrorTemplateById,
        refetch: refetchDbTemplateById,
    };

    const loadingContent = dbLoadingTemplateById || loading;

    if (loadingContent) {
        return <div>{"Loading..."}</div>;
    }

    const emailTitle = (mailDataType: MailDataType, templateData: DbTemplate["data"]) => {
        if (mailDataType === MailDataType.Invoice) {
            return (
                <Space>
                    <div style={{color: grey[3]}}>{"Vorschau:"}</div>
                    <span>{"Email an Rechnungsempfänger"}</span>
                </Space>
            );
        }

        return (
            <Space>
                <div style={{color: grey[3]}}>{"Vorschau:"}</div>
                {`${templateData?.template?.name}`}
            </Space>
        );
    };

    const isNoTemplate = dbTemplate.data?.template.templateType === TemplateType.NoTemplate;

    if (dbTemplate.data) {
        return (
            <Modal
                title={isNoTemplate ? "Vorschau Email ohne Vorlage" : emailTitle(mailDataType, dbTemplate.data)}
                open={Boolean(selectedTemplate)}
                onCancel={() => {
                    if (mailDataType === MailDataType.Staff) {
                        closeAll?.();
                    }
                    setSelectedTemplate(undefined);
                }}
                destroyOnClose={true}
                bodyStyle={{
                    maxHeight: "calc(100vh - 200px)",
                    padding: "0px",
                }}
                style={{top: 40}}
                width={800}
                footer={[
                    <EmailFooter
                        key="emailFooter"
                        dbTemplate={dbTemplate}
                        editMode={editMode}
                        setEditMode={setEditMode}
                        formikProps={formikProps as FormikProps<EditTemplateInput & {id: string}>}
                        draft={draft}
                        selection={selection}
                        mailDataType={mailDataType}
                        closeAll={() => {
                            setSelectedTemplate(undefined);
                            closeAll?.();
                        }}
                        attachments={attachments}
                        disabledSendButton={disabledSendButton}
                        notifySelection={notifySelection}
                        setSelectedTemplateUndefined={() => {
                            if (mailDataType === MailDataType.Staff) {
                                closeAll?.();
                            }
                            setSelectedTemplate(undefined);
                        }}
                        showInvoiceAttachments={showInvoiceAttachments}
                        invoiceAttachmentStatus={invoiceAttachmentStatus}
                        reversalInvoiceAttachmentStatus={reversalInvoiceAttachmentStatus}
                        attachInvoices={attachInvoices}
                        setAttachInvoices={setAttachInvoices}
                        attachReversalInvoices={attachReversalInvoices}
                        setAttachReversalInvoices={setAttachReversalInvoices}
                    />,
                ]}
            >
                <Card
                    headStyle={{padding: `0 ${marginS}`}}
                    bodyStyle={{
                        padding: `${marginXs} 0`,
                        overflow: "auto",
                    }}
                    size="small"
                >
                    <>
                        {mailDataType === MailDataType.Course && <CourseBookingTypeSelector selection={selection} dbTemplateId={dbTemplate.data.template.id} setNotifySelection={setNotifySelection} />}
                        <ReceiversBar
                            mailDataType={mailDataType}
                            selection={selection}
                            notifySelection={notifySelection}
                            setDisabledSendButton={setDisabledSendButton}
                            setReceiverCount={setReceiverCount}
                        />
                        {mailDataType !== MailDataType.Invoice && (
                            <UploadComponent
                                setAttachments={setAttachments}
                                attachments={attachments}
                                receiverCount={receiverCount}
                                setDisabledSendButton={setDisabledSendButton}
                                attachInvoices={attachInvoices}
                                attachReversalInvoices={attachReversalInvoices}
                                invoiceCount={invoiceCount}
                            />
                        )}
                        {editMode ? (
                            <EmailEditor
                                // Vorlagenwechsel im Editor-Modus: Formik muss neu mounten, sonst
                                // behält es die Werte der vorherigen Vorlage.
                                key={templateId}
                                dbTemplate={dbTemplate}
                                mailDataType={mailDataType}
                                setFormikProps={setFormikProps}
                                setEditMode={setEditMode}
                                isNoTemplate={isNoTemplate}
                                draft={draft}
                                setDraft={setDraft}
                            />
                        ) : (
                            <EmailPreview
                                dbTemplate={dbTemplate}
                                mailDataType={mailDataType}
                                ids={selection.map((s) => s.id)}
                                draft={draft}
                                notifySelection={notifySelection}
                            />
                        )}
                    </>
                </Card>
            </Modal>
        );
    }

    return <div>{"Keine Vorlagen gefunden"}</div>;
};

export default memo(Email);
